import { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { 
  Menu, X, Phone, Mail, MapPin, Clock, 
  Wrench, Factory, Flame, Shield, Settings,
  ChevronRight, ArrowRight, MessageCircle,
  Building2, Users, Award, CheckCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Services data
const services = [
  {
    icon: Factory,
    title: "Fabricación de Tanques",
    description: "Tanques de almacenamiento, presión atmosférica y recipientes especiales en acero al carbón e inoxidable."
  },
  {
    icon: Settings,
    title: "Tuberías Industriales",
    description: "Fabricación, habilitación y montaje de líneas de tubería para servicios industriales diversos."
  },
  {
    icon: Flame,
    title: "Pailería Especializada",
    description: "Trabajos de pailería de alta precisión para la industria manufacturera y petroquímica."
  },
  {
    icon: Wrench,
    title: "Soldadura Calificada",
    description: "Personal certificado en soldadura para acero inoxidable, carbón y aluminio."
  },
  {
    icon: Shield,
    title: "Montaje Industrial",
    description: "Montaje y desmontaje de equipos, estructuras metálicas y maquinaria industrial."
  }
];

// Clients
const clients = ["CELANESE", "DEACERO", "ALSTOM POWER", "TRON HERMANOS", "QUIMIC", "CRISOBA"];

// Stats
const stats = [
  { number: "+45", label: "Años de Experiencia", suffix: "" },
  { number: "+100", label: "Proyectos Completados", suffix: "" },
  { number: "+20", label: "Clientes Industriales", suffix: "" },
  { number: "24/7", label: "Soporte Técnico", suffix: "" }
];

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    empresa: "",
    email: "",
    telefono: "",
    mensaje: ""
  });
  const [formStatus, setFormStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: "", message: "" });

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setFormStatus({ type: "success", message: "¡Mensaje enviado! Nos pondremos en contacto pronto." });
        setFormData({ nombre: "", empresa: "", email: "", telefono: "", mensaje: "" });
      }
    } catch (error) {
      setFormStatus({ type: "error", message: "Error al enviar. Intente por WhatsApp." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  const openWhatsApp = () => {
    window.open("https://api.whatsapp.com/send?phone=524437320907&text=Hola,%20me%20interesa%20cotizar%20un%20proyecto%20con%20MATISA", "_blank");
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-sm border-b-2 border-zinc-100 shadow-sm" : "bg-transparent"
        }`}
        data-testid="header"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#" className="flex items-center gap-3" data-testid="logo">
              <div className="w-10 h-10 bg-green-600 flex items-center justify-center">
                <Factory className="w-6 h-6 text-white" />
              </div>
              <span className={`font-black text-2xl tracking-tighter ${scrolled ? "text-zinc-900" : "text-white"}`}>
                MATISA
              </span>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              {["Inicio", "Servicios", "Nosotros", "Materiales", "Contacto"].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`text-sm font-semibold uppercase tracking-wider transition-colors ${
                    scrolled ? "text-zinc-600 hover:text-green-600" : "text-white/80 hover:text-white"
                  }`}
                  data-testid={`nav-${item.toLowerCase()}`}
                >
                  {item}
                </button>
              ))}
              <button
                onClick={openWhatsApp}
                className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide px-6 py-3 transition-colors flex items-center gap-2"
                data-testid="nav-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" />
                Cotizar
              </button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? (
                <X className={`w-6 h-6 ${scrolled ? "text-zinc-900" : "text-white"}`} />
              ) : (
                <Menu className={`w-6 h-6 ${scrolled ? "text-zinc-900" : "text-white"}`} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-zinc-100" data-testid="mobile-menu">
            <div className="px-6 py-4 space-y-4">
              {["Inicio", "Servicios", "Nosotros", "Materiales", "Contacto"].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="block w-full text-left text-zinc-600 font-semibold uppercase tracking-wide py-2"
                  data-testid={`mobile-nav-${item.toLowerCase()}`}
                >
                  {item}
                </button>
              ))}
              <button
                onClick={openWhatsApp}
                className="w-full bg-green-600 text-white font-bold uppercase tracking-wide px-6 py-3 flex items-center justify-center gap-2"
                data-testid="mobile-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" />
                Cotizar por WhatsApp
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section
        id="inicio"
        className="relative min-h-screen flex items-center"
        data-testid="hero-section"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/27382493/pexels-photo-27382493.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
          }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/70 to-black/50" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 py-32">
          <div className="max-w-3xl">
            <p className="text-sm tracking-[0.2em] uppercase font-bold text-green-500 mb-6 animate-fade-in-up opacity-0" style={{animationDelay: '0.1s'}}>
              Desde 1979 | Morelia, Michoacán
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-black uppercase text-white mb-6 animate-fade-in-up opacity-0" style={{animationDelay: '0.2s'}}>
              Precisión y Fuerza en Pailería Industrial
            </h1>
            <p className="text-lg sm:text-xl text-zinc-300 mb-10 leading-relaxed animate-fade-in-up opacity-0" style={{animationDelay: '0.3s'}}>
              Fabricación de tanques, tuberías y pailería de alto rendimiento para el sector industrial. 
              Más de 45 años respaldando a la industria mexicana.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up opacity-0" style={{animationDelay: '0.4s'}}>
              <button
                onClick={openWhatsApp}
                className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide px-8 py-4 transition-colors flex items-center justify-center gap-2"
                data-testid="hero-cta-cotizar"
              >
                Cotizar Proyecto
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => scrollToSection("materiales")}
                className="border-2 border-white text-white hover:bg-white hover:text-zinc-900 font-bold uppercase tracking-wide px-8 py-4 transition-all flex items-center justify-center gap-2"
                data-testid="hero-cta-materiales"
              >
                Venta de Materiales
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/50 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* Clients Marquee */}
      <section className="bg-zinc-900 py-8 border-y border-zinc-800" data-testid="clients-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-16">
            {clients.map((client, index) => (
              <span
                key={index}
                className="client-logo text-zinc-500 hover:text-green-500 font-black text-lg sm:text-xl tracking-[0.15em] uppercase transition-colors cursor-default"
                data-testid={`client-${client.toLowerCase().replace(' ', '-')}`}
              >
                {client}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-24 sm:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.2em] uppercase font-bold text-green-600 mb-4">Nuestros Servicios</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-zinc-900">
              Soluciones Integrales para la Industria
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-zinc-200">
            {services.map((service, index) => (
              <div
                key={index}
                className="service-card group relative border border-zinc-200 bg-white p-8 hover:border-green-500 transition-all duration-300 hover:bg-zinc-50"
                data-testid={`service-card-${index}`}
              >
                <service.icon className="service-icon w-12 h-12 text-zinc-400 mb-6 transition-all duration-300" />
                <h3 className="text-xl font-bold text-zinc-900 mb-3">{service.title}</h3>
                <p className="text-zinc-600 leading-relaxed">{service.description}</p>
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-6 h-6 text-green-600" />
                </div>
              </div>
            ))}
            {/* CTA Card */}
            <div className="service-card group relative border border-zinc-200 bg-green-600 p-8 flex flex-col justify-center items-center text-center hover:bg-green-700 transition-colors">
              <h3 className="text-xl font-bold text-white mb-3">¿Necesita una cotización?</h3>
              <p className="text-green-100 mb-6">Contáctenos hoy mismo</p>
              <button
                onClick={openWhatsApp}
                className="bg-white text-green-600 hover:bg-zinc-100 font-bold uppercase tracking-wide px-6 py-3 transition-colors flex items-center gap-2"
                data-testid="services-cta-btn"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About / Stats Section */}
      <section id="nosotros" className="py-24 sm:py-32 bg-zinc-900" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Content */}
            <div>
              <p className="text-sm tracking-[0.2em] uppercase font-bold text-green-500 mb-4">Sobre Nosotros</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-bold text-white mb-6">
                +45 Años de Experiencia Industrial
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-6">
                <strong className="text-white">MATISA</strong> (Manufacturas y Talleres Industriales S.A. de C.V.) 
                inició operaciones en 1979 en Zacapu, Michoacán. Hoy somos líderes en el estado 
                en fabricación y mantenimiento industrial.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold">Misión</h4>
                    <p className="text-zinc-400">Proveer soluciones metalmecánicas de la más alta calidad y seguridad para nuestros clientes.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold">Visión</h4>
                    <p className="text-zinc-400">Ser la empresa líder en Michoacán en construcción y mantenimiento industrial.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="text-white font-semibold">Personal Certificado</h4>
                    <p className="text-zinc-400">Contamos con manuales de calidad, seguridad y personal certificado.</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => scrollToSection("contacto")}
                className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide px-8 py-4 transition-colors flex items-center gap-2"
                data-testid="about-cta-btn"
              >
                Conocer Más
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            {/* Image + Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <img
                  src="https://images.unsplash.com/photo-1582489852031-9a1ad48ed893?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwd2VsZGluZyUyMHdvcmtlcnxlbnwwfHx8fDE3NzU2OTA2NzV8MA&ixlib=rb-4.1.0&q=85&w=600"
                  alt="Trabajador de soldadura MATISA"
                  className="w-full h-64 object-cover"
                  data-testid="about-image"
                />
              </div>
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="p-6 border border-zinc-800 bg-zinc-900/50"
                  data-testid={`stat-${index}`}
                >
                  <p className="text-3xl sm:text-4xl font-black text-green-500 mb-1">
                    {stat.number}
                  </p>
                  <p className="text-zinc-400 text-sm uppercase tracking-wide">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Materials Section - Coming Soon */}
      <section id="materiales" className="py-24 sm:py-32 bg-white" data-testid="materials-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="relative border-2 border-dashed border-green-500 p-12 text-center">
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-2 text-sm font-bold uppercase tracking-widest">
              Próximamente
            </div>
            
            <div className="max-w-2xl mx-auto">
              <Building2 className="w-16 h-16 text-green-600 mx-auto mb-6" />
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-zinc-900 mb-4">
                Venta de Materiales
              </h2>
              <p className="text-zinc-600 text-lg mb-8">
                Muy pronto podrás adquirir placas de acero, materiales de herrería y más. 
                Déjanos tus datos y te avisamos cuando esté disponible.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
                <div className="p-4 border border-zinc-200">
                  <p className="font-bold text-zinc-900">Placas de Acero</p>
                  <p className="text-sm text-zinc-500">Carbón e Inoxidable</p>
                </div>
                <div className="p-4 border border-zinc-200">
                  <p className="font-bold text-zinc-900">Perfiles</p>
                  <p className="text-sm text-zinc-500">PTR, Ángulos, Vigas</p>
                </div>
                <div className="p-4 border border-zinc-200">
                  <p className="font-bold text-zinc-900">Tubería</p>
                  <p className="text-sm text-zinc-500">Diversos diámetros</p>
                </div>
              </div>

              <button
                onClick={openWhatsApp}
                className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide px-8 py-4 transition-colors inline-flex items-center gap-2"
                data-testid="materials-cta-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Solicitar Cotización
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-24 sm:py-32 bg-zinc-50" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <div>
              <p className="text-sm tracking-[0.2em] uppercase font-bold text-green-600 mb-4">Contacto</p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-zinc-900 mb-6">
                ¿Listo para tu próximo proyecto?
              </h2>
              <p className="text-zinc-600 text-lg mb-8">
                Contáctanos para cotizar tu proyecto. Respondemos en menos de 24 horas.
              </p>

              <div className="space-y-6">
                <a
                  href="https://api.whatsapp.com/send?phone=524437320907"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 hover:bg-green-100 transition-colors"
                  data-testid="contact-whatsapp"
                >
                  <div className="w-12 h-12 bg-green-600 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-zinc-900">WhatsApp</p>
                    <p className="text-green-600">+52 443 732 0907</p>
                  </div>
                </a>

                <a
                  href="tel:+524435209254"
                  className="flex items-center gap-4 p-4 border border-zinc-200 hover:bg-zinc-50 transition-colors cursor-pointer"
                  data-testid="contact-phone"
                >
                  <div className="w-12 h-12 bg-zinc-900 flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-zinc-900">Teléfonos</p>
                    <p className="text-zinc-600">(443) 520-9254 / 520-9255</p>
                    <p className="text-xs text-green-600 mt-1">Toca para llamar</p>
                  </div>
                </a>

                <a
                  href="mailto:talleres_industriales@yahoo.com.mx"
                  className="flex items-center gap-4 p-4 border border-zinc-200 hover:bg-zinc-50 transition-colors cursor-pointer"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 bg-zinc-900 flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-zinc-900">Correo</p>
                    <p className="text-zinc-600">talleres_industriales@yahoo.com.mx</p>
                    <p className="text-xs text-green-600 mt-1">Toca para enviar email</p>
                  </div>
                </a>

                <a
                  href="https://www.google.com/maps/search/?api=1&query=Morelia+Michoacan+Mexico+MATISA"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 border border-zinc-200 hover:bg-zinc-50 transition-colors cursor-pointer"
                  data-testid="contact-location"
                >
                  <div className="w-12 h-12 bg-zinc-900 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-zinc-900">Ubicación</p>
                    <p className="text-zinc-600">Morelia, Michoacán, México</p>
                    <p className="text-xs text-green-600 mt-1">Toca para ver en Google Maps</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 border border-zinc-200">
              <h3 className="text-xl font-bold text-zinc-900 mb-6">Envíanos un mensaje</h3>
              
              {formStatus.message && (
                <div
                  className={`p-4 mb-6 ${
                    formStatus.type === "success" 
                      ? "bg-green-50 text-green-800 border border-green-200" 
                      : "bg-red-50 text-red-800 border border-red-200"
                  }`}
                  data-testid="form-status"
                >
                  {formStatus.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-bold uppercase tracking-widest text-zinc-500 mb-2">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    className="w-full border-b-2 border-zinc-300 bg-transparent px-0 py-3 text-zinc-900 placeholder:text-zinc-400 focus:border-green-600"
                    placeholder="Tu nombre completo"
                    data-testid="input-nombre"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold uppercase tracking-widest text-zinc-500 mb-2">
                    Empresa
                  </label>
                  <input
                    type="text"
                    name="empresa"
                    value={formData.empresa}
                    onChange={handleInputChange}
                    className="w-full border-b-2 border-zinc-300 bg-transparent px-0 py-3 text-zinc-900 placeholder:text-zinc-400 focus:border-green-600"
                    placeholder="Nombre de tu empresa"
                    data-testid="input-empresa"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold uppercase tracking-widest text-zinc-500 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full border-b-2 border-zinc-300 bg-transparent px-0 py-3 text-zinc-900 placeholder:text-zinc-400 focus:border-green-600"
                      placeholder="correo@ejemplo.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold uppercase tracking-widest text-zinc-500 mb-2">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      name="telefono"
                      value={formData.telefono}
                      onChange={handleInputChange}
                      className="w-full border-b-2 border-zinc-300 bg-transparent px-0 py-3 text-zinc-900 placeholder:text-zinc-400 focus:border-green-600"
                      placeholder="(443) 123-4567"
                      data-testid="input-telefono"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold uppercase tracking-widest text-zinc-500 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full border-b-2 border-zinc-300 bg-transparent px-0 py-3 text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 resize-none"
                    placeholder="Describe tu proyecto o requerimiento..."
                    data-testid="input-mensaje"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-bold uppercase tracking-wide px-8 py-4 transition-colors flex items-center justify-center gap-2"
                  data-testid="submit-btn"
                >
                  {isSubmitting ? "Enviando..." : "Enviar Mensaje"}
                  {!isSubmitting && <ArrowRight className="w-5 h-5" />}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-900 py-12 border-t border-zinc-800" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-600 flex items-center justify-center">
                  <Factory className="w-6 h-6 text-white" />
                </div>
                <span className="font-black text-2xl tracking-tighter text-white">MATISA</span>
              </div>
              <p className="text-zinc-400 text-sm">
                Manufacturas y Talleres Industriales S.A. de C.V.<br />
                Desde 1979 al servicio de la industria.
              </p>
            </div>
            <div>
              <h4 className="text-white font-bold uppercase tracking-wide mb-4">Servicios</h4>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li>Fabricación de Tanques</li>
                <li>Tuberías Industriales</li>
                <li>Pailería y Soldadura</li>
                <li>Montaje Industrial</li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold uppercase tracking-wide mb-4">Contacto</h4>
              <ul className="space-y-2 text-zinc-400 text-sm">
                <li>+52 443 732 0907 (WhatsApp)</li>
                <li>(443) 520-9254 / 520-9255</li>
                <li>talleres_industriales@yahoo.com.mx</li>
                <li>Morelia, Michoacán</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-8 text-center">
            <p className="text-zinc-500 text-sm">
              © {new Date().getFullYear()} MATISA. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href="https://api.whatsapp.com/send?phone=524437320907&text=Hola,%20me%20interesa%20cotizar%20un%20proyecto%20con%20MATISA"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-24 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg shadow-green-500/30 z-50 flex items-center justify-center hover:scale-105 transition-transform duration-200 whatsapp-pulse"
        data-testid="floating-whatsapp-btn"
        aria-label="Contactar por WhatsApp"
      >
        <MessageCircle className="w-7 h-7" />
      </a>
    </div>
  );
}

export default App;

